import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Cashpurr, CashpurrMessage } from '@/components/ui/cashpurr';
import { CheckCircle, Target, TrendingUp, Users, Zap, Star, ArrowRight, Play, Shield, Clock, Brain, Rocket, X, Calendar, BarChart3, Crown, MessageSquare, BookOpen, Trophy } from 'lucide-react';
import DemoMode from '@/components/demo/DemoMode';

const Home: React.FC = () => {  
  const [showDemo, setShowDemo] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section with Oceanic Gradient */}
      <section className="relative overflow-hidden pt-32 pb-40 bg-gradient-to-br from-blue-600 via-teal-500 to-orange-400">
        <div className="absolute inset-0 bg-dot-white/10" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-5xl mx-auto">
            <div className="flex justify-center mb-8 animate-fade-in">
              <div className="relative">
                <Cashpurr size="xl" mood="happy" className="drop-shadow-2xl" />
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
              </div>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8 leading-tight text-white drop-shadow-lg animate-fade-in">
              Turn your startup idea into
              <br />
              <span className="bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent">
                revenue in 30 days
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed max-w-3xl mx-auto font-light animate-fade-in">
              AI-powered validation, daily accountability, and proven playbooks. 
              Join 500+ founders who turned ideas into revenue.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16 animate-fade-in">
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-300 hover:to-orange-400 text-lg px-12 py-6 font-semibold transition-all duration-300 group shadow-2xl hover:shadow-yellow-400/25 hover:scale-105">
                  Start Your 30-Day Sprint
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white/80 text-black hover:bg-white hover:text-teal-600 text-lg px-12 py-6 font-semibold transition-all duration-300 group backdrop-blur-sm"
                onClick={() => setShowDemo(true)}
              >
                <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                Watch Demo
              </Button>
            </div>

            {/* Social Proof */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-white/90">
              <div className="flex items-center gap-2">
                <span className="ml-2 font-medium">500+ entrepreneurs made their first sale</span>
              </div>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
                <span className="ml-2 font-medium">4.9/5 success rate</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="py-32 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-800">Everything You Need to Sprint</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Powerful tools designed specifically for solo founders and startup sprinters
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Brain,
                title: "AI Idea Validation",
                description: "Get brutal, honest feedback on your startup idea before you waste time building.",
                color: "text-blue-600",
                gradient: "from-blue-500 to-teal-500"
              },
              {
                icon: Zap,
                title: "Boss Mode",
                description: "Daily accountability with Cashpurr keeping you on track every single day.",
                color: "text-teal-600", 
                gradient: "from-teal-500 to-cyan-500"
              },
              {
                icon: TrendingUp,
                title: "ROI Tracking",
                description: "Track every penny spent and earned. See exactly where every dollar goes and when you hit profitability.",
                color: "text-orange-600",
                gradient: "from-orange-500 to-yellow-500"
              },
              {
                icon: BookOpen,
                title: "Launch Playbooks",
                description: "Proven templates and strategies from founders who've been there.",
                color: "text-purple-600",
                gradient: "from-purple-500 to-pink-500"
              }
            ].map((feature, index) => (
              <Card key={index} className="group relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white/80 backdrop-blur-sm">
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                <CardHeader className="text-center pb-6">
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${feature.gradient} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-slate-800 group-hover:text-slate-900">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 leading-relaxed text-center">
                    {feature.description}
                  </p>
                </CardContent>
                <div className={`absolute -bottom-2 -left-2 -right-2 h-1 bg-gradient-to-r ${feature.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Inspiration Video Section */}
      <section className="py-32 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Why Most Ideas Never Become Reality</h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto mb-12">
              Jeff Bezos explains the brutal truth about turning ideas into successful products. 
              Don't let your idea become another statistic.
            </p>
            
            <div className="max-w-4xl mx-auto">
              <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black/20 backdrop-blur-sm border border-white/10">
                <iframe
                  src="https://www.youtube-nocookie.com/embed/vigaGfM0jCo?autoplay=0&controls=1&rel=0&modestbranding=1&showinfo=0"
                  title="Jeff Bezos on Ideas vs Execution"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  frameBorder="0"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
              <p className="text-purple-200 mt-6 text-lg italic">
                "Ideas are easy. Execution is everything." - Jeff Bezos
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">How It Works</h2>
            <p className="text-xl text-blue-200 max-w-2xl mx-auto">
              Three proven steps that convert ideas into revenue
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-16">
            {[
              {
                step: "01",
                icon: Brain,
                title: "Validate Your Idea",
                description: "Get AI-powered feedback and brutal honesty about your startup concept.",
                mascot: "thinking"
              },
              {
                step: "02", 
                icon: Rocket,
                title: "Sprint with Accountability",
                description: "Follow daily tasks with Cashpurr keeping you motivated and on track.",
                mascot: "happy"
              },
              {
                step: "03",
                icon: Trophy,
                title: "Launch & Track ROI", 
                description: "Use proven playbooks to launch and celebrate your first paying customer.",
                mascot: "excited"
              }
            ].map((step, index) => (
              <div key={index} className="text-center group">
                <div className="relative mb-8">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-r from-teal-400 to-blue-500 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="text-6xl font-thin text-teal-400/30 mb-4">{step.step}</div>
                <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-teal-300 transition-colors">{step.title}</h3>
                <p className="text-blue-200 leading-relaxed max-w-sm mx-auto">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Pods Section */}
      <section className="py-32 bg-gradient-to-r from-teal-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="relative overflow-hidden border-0 shadow-2xl bg-gradient-to-br from-white to-teal-50/50">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-400 to-blue-500 rounded-bl-full opacity-10"></div>
              <CardHeader className="text-center pb-8">
                <div className="flex justify-center mb-6">
                  <Users className="w-16 h-16 text-teal-600" />
                </div>
                <CardTitle className="text-3xl font-bold text-slate-800 mb-4">Join Community Pods</CardTitle>
                <CardDescription className="text-xl text-slate-600 max-w-2xl mx-auto">
                  Connect with fellow solo founders in small, focused groups for accountability, support, and shared wins.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="flex justify-center items-center gap-4 mb-8">
                  <div className="flex -space-x-3">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-400 to-blue-500 border-4 border-white flex items-center justify-center text-white font-bold">
                        {String.fromCharCode(64 + i)}
                      </div>
                    ))}
                  </div>
                  <MessageSquare className="w-8 h-8 text-teal-600" />
                </div>
                <Link to="/signup">
                  <Button size="lg" className="bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-400 hover:to-blue-500 text-white px-8 py-4 text-lg font-semibold shadow-lg">
                    Join a Pod Today
                    <Users className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ROI/Success Showcase */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-800 mb-6">Track Your Path to Profit</h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Watch your investment turn into revenue with our comprehensive ROI tracking. 
                See exactly where every dollar goes and when you hit profitability.
              </p>
              
              {/* Mock Chart */}
              <Card className="bg-gradient-to-br from-slate-50 to-white border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-slate-800">
                    <BarChart3 className="w-5 h-5 text-teal-600" />
                    Revenue Growth
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Month 1</span>
                      <div className="flex-1 mx-4">
                        <Progress value={25} className="h-3" />
                      </div>
                      <span className="font-semibold text-green-600">$1,250</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Month 2</span>
                      <div className="flex-1 mx-4">
                        <Progress value={60} className="h-3" />
                      </div>
                      <span className="font-semibold text-green-600">$3,200</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Month 3</span>
                      <div className="flex-1 mx-4">
                        <Progress value={90} className="h-3" />
                      </div>
                      <span className="font-semibold text-green-600">$5,800</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Testimonials */}
            <div className="space-y-6">
              {[
                {
                  name: "Priya S.",
                  role: "SaaS Founder",
                  content: "Made ₹2L in my first month after the sprint. The AI validation saved me from building the wrong product!",
                  avatar: "P",
                  gradient: "from-blue-500 to-teal-500"
                },
                {
                  name: "Rahul M.", 
                  role: "E-commerce Entrepreneur",
                  content: "Cashpurr's accountability was exactly what I needed. Launched my store and got 50 customers in 30 days.",
                  avatar: "R",
                  gradient: "from-teal-500 to-cyan-500"
                },
                {
                  name: "Anita K.",
                  role: "Course Creator", 
                  content: "The playbooks are gold. Followed the launch template and pre-sold ₹5L worth of courses before building!",
                  avatar: "A",
                  gradient: "from-orange-500 to-yellow-500"
                }
              ].map((testimonial, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${testimonial.gradient} flex items-center justify-center text-white font-bold text-lg`}>
                        {testimonial.avatar}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-1 mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star key={star} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                        <p className="text-slate-700 mb-3 italic">"{testimonial.content}"</p>
                        <div>
                          <div className="font-semibold text-slate-800">{testimonial.name}</div>
                          <div className="text-sm text-slate-600">{testimonial.role}</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Founder Journal Highlight */}
      <section className="py-32 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <Card className="bg-white shadow-2xl border-0 transform rotate-3 hover:rotate-0 transition-transform duration-300">
                <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Founder Journal - Day 15
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-slate-700 mb-4">
                    "Today I validated my SaaS idea with 5 potential customers. 3 said they'd pay $50/month! 
                    The AI feedback was spot-on about the pricing concerns..."
                  </p>
                  <div className="flex items-start gap-3 bg-blue-50 p-3 rounded-lg">
                    <Cashpurr size="sm" mood="thinking" />
                    <div className="bg-white p-2 rounded-lg shadow-sm flex-1">
                      <p className="text-sm text-slate-600">
                        Great progress! Now focus on those 3 interested customers. 
                        Can you get them to pre-order? 💪
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <h2 className="text-4xl font-bold text-slate-800 mb-6">Reflect & Iterate with AI</h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Your founder journal becomes smarter with AI insights. Track your thoughts, 
                challenges, and wins while getting personalized feedback from Cashpurr.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Daily reflection prompts",
                  "AI-powered insights and suggestions", 
                  "Track emotional journey",
                  "Celebrate small wins"
                ].map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-400 hover:to-purple-500 text-white text-base py-3 font-semibold">
                  Start Journaling
                  <BookOpen className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-800">Simple, Transparent Pricing</h2>
            <p className="text-xl text-slate-600">
              Start building your business today. Cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">            
            {/* Free Plan */}
            <Card className="border-2 border-slate-200 hover:border-teal-300 transition-colors duration-300">
              <CardHeader className="text-center pb-6">
                <Badge variant="outline" className="mx-auto mb-4">Free</Badge>
                <CardTitle className="text-2xl font-bold mb-4 text-slate-800">Streak Starter</CardTitle>
                <div className="text-4xl font-bold text-slate-800">$0</div>
                <p className="text-slate-600 mt-2">Perfect for getting started</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {[
                    "Dashboard (view-only tasks)",
                    "1 Idea Validation per month", 
                    "Basic ROI Tracker",
                    "Community access (read-only)"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="block">
                  <Button variant="outline" className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 text-base py-3 font-medium">
                    Start Free
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Pro Plan */}
            <Card className="border-4 border-gradient-to-r from-yellow-400 to-orange-500 relative transform scale-105 shadow-2xl">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-4 py-1">
                  Most Popular
                </Badge>
              </div>
              <CardHeader className="text-center pb-6 bg-gradient-to-br from-yellow-50 to-orange-50">
                <Badge variant="secondary" className="mx-auto mb-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-black">Monthly</Badge>
                <CardTitle className="text-2xl font-bold mb-4 text-slate-800">Monthly Pro</CardTitle>
                <div className="text-4xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">$5.99</div>
                <p className="text-slate-600 mt-2">Full access to everything</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {[
                    "Unlimited AI Idea Validation",
                    "Full Dashboard & Task Management", 
                    "Founder Journal with AI insights",
                    "Boss Mode accountability",
                    "Advanced ROI Tracking",
                    "Community Pods access",
                    "Launch Playbooks & Templates",
                    "Priority Support"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-orange-600 flex-shrink-0" />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="block">
                  <Button className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-base py-3 font-semibold shadow-lg">
                    Start Pro Monthly
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Yearly Plan */}
            <Card className="border-2 border-slate-200 hover:border-purple-300 transition-colors duration-300">
              <CardHeader className="text-center pb-6">
                <Badge variant="outline" className="mx-auto mb-4">Yearly</Badge>
                <CardTitle className="text-2xl font-bold mb-4 text-slate-800">Yearly Pro</CardTitle>
                <div className="text-4xl font-bold text-slate-800">$59.99</div>
                <p className="text-slate-600 mt-2">2 months free ($4.99/month)</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {[
                    "Everything in Monthly Pro",
                    "2 months free (save $12)",
                    "Exclusive yearly masterclasses", 
                    "1-on-1 strategy session",
                    "Advanced analytics dashboard",
                    "Custom playbook creation",
                    "Priority feature requests"
                  ].map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0" />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="block">
                  <Button className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-400 hover:to-purple-500 text-white text-base py-3 font-semibold">
                    Start Pro Yearly
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-8">
              <div className="relative">
                <Cashpurr size="xl" mood="excited" className="drop-shadow-2xl" />
              </div>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
              Your First Paying Customer is 
              <span className="bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent">
                30 Days Away
              </span>
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-blue-200">
              Stop overthinking. Start sprinting. Join the community that turns ideas into income.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-xl px-16 py-6 font-bold shadow-2xl hover:shadow-yellow-400/25 hover:scale-105 transition-all duration-300">
                  Start Your Streak Now
                  <Rocket className="ml-3 h-6 w-6" />
                </Button>
              </Link>
              <div className="flex items-center gap-3 text-blue-200">
                <Clock className="w-5 h-5" />
                <span>Takes 2 minutes to start</span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-blue-200">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">30</div>
                <div className="text-sm uppercase tracking-wide">Days to Launch</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">500+</div>
                <div className="text-sm uppercase tracking-wide">Success Stories</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">₹30L+</div>
                <div className="text-sm uppercase tracking-wide">Revenue Generated</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">85%</div>
                <div className="text-sm uppercase tracking-wide">Success Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Mode */}
      {showDemo && <DemoMode onClose={() => setShowDemo(false)} />}
    </div>
  );
};

export default Home;
